// PlugitAI light-theme helpers.
//
// Aug 2026 design shift: marketing pages move from dark charcoal to premium
// white / very-light-slate with a subtle circuit-board watermark and blue
// accents. Kit's brief: "clean, expensive, technical, fast, credible and
// custom — not a generic AI template."
//
// Two exports:
//   • LIGHT_BG  — inline style object with the watermark background, layer on
//                 <main> or any full-width canvas.
//   • CircuitOverlay — a decorative absolute-positioned <svg> for pages that
//                 want a stronger circuit pattern (used sparingly).
//
// Keep the pattern near watermark strength (<= ~8% opacity) so copy stays
// readable at every viewport.

// Kit's 3D-embossed circuit-board texture — darkened + contrast-boosted so
// the shadow definition reads as premium/dramatic (Aug 2026 tune-up).
// Layer stack (top → bottom):
//   1. Slate vignette   — subtle radial darkening at corners for drama
//   2. White veil       — softer 66→56% gradient so texture shadows show
//   3. Kit's circuit jpg — full-contrast texture underneath
// Copy on top stays WCAG-AA readable; cards + hero copy pop.
export const LIGHT_BG = {
  background: [
    "radial-gradient(ellipse 1400px 900px at 50% 30%, transparent 0%, rgba(15,23,42,0.06) 65%, rgba(15,23,42,0.14) 100%)",
    "linear-gradient(180deg, rgba(255,255,255,0.66) 0%, rgba(248,250,252,0.58) 50%, rgba(241,245,249,0.56) 100%)",
    "url(/circuit-white-bg.jpg)",
  ].join(","),
  backgroundColor: "#F1F5F9",
  backgroundSize: "100% 100%, 100% 100%, 1200px auto",
  backgroundPosition: "0 0, 0 0, center top",
  backgroundRepeat: "no-repeat, no-repeat, repeat",
  backgroundAttachment: "fixed, fixed, fixed",
};

// Optional stronger circuit-board SVG for hero sections. Renders absolute so
// callers control positioning + z-index. Currents stay at ~6% opacity.
export function CircuitOverlay({ opacity = 0.06, color = "#1E5FE0" }) {
  return (
    <svg
      aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id="pcb" width="120" height="120" patternUnits="userSpaceOnUse">
          {/* Node dots */}
          <circle cx="10" cy="10" r="1.5" fill={color} />
          <circle cx="110" cy="10" r="1.5" fill={color} />
          <circle cx="60" cy="60" r="2" fill={color} />
          <circle cx="10" cy="110" r="1.5" fill={color} />
          <circle cx="110" cy="110" r="1.5" fill={color} />
          {/* Traces */}
          <path d="M10 10 L60 10 L60 60" stroke={color} strokeWidth="0.6" fill="none" />
          <path d="M110 10 L110 60 L60 60" stroke={color} strokeWidth="0.6" fill="none" />
          <path d="M10 110 L60 110 L60 60" stroke={color} strokeWidth="0.6" fill="none" />
          <path d="M110 110 L60 110" stroke={color} strokeWidth="0.6" fill="none" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#pcb)" />
    </svg>
  );
}

// Shared class strings so we don't hand-tune every page. Keep the palette
// consistent: slate-900 ink, slate-600 body, slate-400 muted, blue-600 accent.
export const LIGHT_CLASSES = {
  page:            "min-h-screen text-slate-900",
  eyebrow:         "text-[10px] md:text-[11px] uppercase tracking-[0.32em] font-black text-blue-600",
  h1:              "font-black leading-[1.02] tracking-[-0.02em] text-slate-900",
  h2:              "font-black text-slate-900 tracking-[-0.02em]",
  body:            "text-slate-600 leading-relaxed",
  muted:           "text-slate-500",
  card:            "rounded-lg border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_16px_rgba(15,23,42,0.06)] transition-shadow",
  cardPad:         "p-5",
  divider:         "border-slate-200",
  ctaPrimary:      "inline-flex items-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-[0_4px_16px_rgba(30,95,224,0.25)]",
  ctaSecondary:    "inline-flex items-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] border border-slate-300 text-slate-900 hover:border-slate-500 hover:bg-slate-50 transition-colors",
  chipBlue:        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-blue-200 bg-blue-50 text-blue-700",
  chipGreen:       "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-emerald-200 bg-emerald-50 text-emerald-700",
  chipGold:        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-200 bg-amber-50 text-amber-800",
};
